/**
 * UsersPage.tsx — 用户管理（ADM-USR）
 *
 * 1:1 移植自原型 design/Novel-admin/src/pages/users.jsx：
 *   列表 + 详情抽屉 + 封禁 / 白名单 / 发放 BONUS / 风控策略 tab。
 * 取数走 ~/data/queries（useUsersPage / useRiskRules / useDeviceWhitelist / useApps）。
 * 写动作已接真（useMutation + invalidate + 错误 toast，PR1 模式）：
 *   解禁 POST /users/:id/unban · 封禁 / 发 BONUS / 白名单增删在 dialogs/* 内接真 ·
 *   风控启停 PATCH /risk-rules/:id/enabled（失败 bump nonce 重挂 Switch 回滚）·
 *   阈值编辑 PATCH /risk-rules/:id/threshold（RiskRuleThresholdEditDialog）。
 * 契约无端点的动作（站内私信 / 批量 BONUS / 导出 CSV / 删除账户 / 取消订阅等）按计划 §五
 * 裁决 NOT-in-scope：保留视觉但 disabled + title 标注，不留假成功 toast。
 * JSX/className/inline-style 与原型逐字保真，仅做 JS→TS + ESM import 机械转换 + 接真改造。
 */

import { onlineManager, useMutation, useQueryClient } from '@tanstack/react-query';
import { type CSSProperties, type ReactNode, useEffect, useState } from 'react';
import { AdminApiError } from '~/api/errors';
import { useAppScope } from '~/app/app-scope-store';
import { adaptRiskRule } from '~/data/adapters/risk-adapter';
import {
  USER_RECORD_TAB_PAGE_SIZE,
  useApps,
  useDashboard,
  useDeviceWhitelist,
  useRiskRules,
  useSysConfig,
  useUserChapterReadingRecords,
  useUserChapterUnlockRecords,
  useUserConsumes,
  useUserDetail,
  useUserFeedback,
  useUserNovelReadingRecords,
  useUserRecharges,
  useUserStats,
  useUserTagsPage,
  useUsersPage,
} from '~/data/queries';
import type { DeviceWhitelistRow, RiskRuleRow, UserRecordTabQuery, UserRow } from '~/data/queries';
import { riskRulesApi } from '~/hooks/use-risk-rules';
import { usersApi } from '~/hooks/use-users';
import {
  fmtCountry,
  fmtDate,
  fmtDateTime,
  fmtLang,
  fmtSpendByCurrency,
  langName,
} from '~/lib/format';
import { Icons } from '~/lib/icons';
import { useAuthStore } from '~/stores/auth-store';
import { Avatar } from '~/ui/Avatar';
import { ContextMenu } from '~/ui/ContextMenu';
import { ConfirmDialog } from '~/ui/Dialog';
import { FilterButton } from '~/ui/FilterButton';
import { Pagination } from '~/ui/Pagination';
import { SearchInput } from '~/ui/SearchInput';
import { TableSkeleton } from '~/ui/Skeleton';
import { Switch } from '~/ui/Switch';
import { Table } from '~/ui/Table';
import { toast } from '~/ui/Toast';
import { Charts } from '~/ui/charts';
import { UserHeldTags } from './UserHeldTags';
import UserTagsTab from './UserTagsTab';
import { BanUserDialog } from './dialogs/BanUserDialog';
import { BatchGrantBonusDialog } from './dialogs/BatchGrantBonusDialog';
import { DeviceSanctionsDialog } from './dialogs/DeviceSanctionsDialog';
import { GrantBonusDialog } from './dialogs/GrantBonusDialog';
import { RiskRuleThresholdEditDialog } from './dialogs/RiskRuleThresholdEditDialog';
import { WhitelistDialog } from './dialogs/WhitelistDialog';

// 角色不足时写动作入口的统一禁用标注（前端 RBAC 防线；后端 requireRole 才是权威）
const FORBIDDEN_ROLE_TITLE = '权限不足 · 该操作受角色限制（后端 requireRole 为权威）';
const MAX_BATCH_BONUS_SELECTED_USERS = 50;
const UNAVAILABLE_DISPLAY_USER_ID = '不可用';
/** 仅供读屏的视觉隐藏（标准 clip 写法）：不显示、不占流，但仍在无障碍树里可被播报 */
const VISUALLY_HIDDEN_STYLE: CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  margin: -1,
  padding: 0,
  border: 0,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
};

function hasDisplayUserId(displayUserId: string): boolean {
  return displayUserId.trim().length > 0;
}

function formatDisplayUserIdForPresentation(displayUserId: string): string {
  return hasDisplayUserId(displayUserId) ? displayUserId : UNAVAILABLE_DISPLAY_USER_ID;
}

async function copyUserIdToClipboard(
  userId: string,
  idKind: '展示用户 ID' | '内部用户 ID',
): Promise<void> {
  if (idKind === '展示用户 ID' && !hasDisplayUserId(userId)) return;
  try {
    if (!navigator.clipboard?.writeText) throw new Error('Clipboard API unavailable');
    await navigator.clipboard.writeText(userId);
    toast(`已复制${idKind}`, { tone: 'pos', icon: 'IconCopy' });
  } catch {
    toast(`复制${idKind} 失败`, { tone: 'neg', icon: 'IconAlert' });
  }
}

/**
 * 用户管理「封禁 / 解禁」写权限闭集（root/admin）—— 后端 ban/unban 限 root/admin。
 * 前端只做 UX 门控（隐藏 / 禁用入口），后端 requireRole 才是权威（参照 system-config 的 useCanWriteSystemConfig）。
 */
function useCanModerateUsers(): boolean {
  const role = useAuthStore((s) => s.user?.role);
  return role === 'root' || role === 'admin';
}

/**
 * 发放 BONUS 写权限（root 专属）—— 后端 POST /finance/grant-bonus 限 root（money 路径）。
 * 前端只做 UX 门控，后端 requireRole('root') 为权威。
 */
function useCanGrantBonus(): boolean {
  const role = useAuthStore((s) => s.user?.role);
  return role === 'root';
}

// 二次确认弹窗参数（替代原型 window.openDialog('confirm', {...})）
interface ConfirmState {
  title: ReactNode;
  message: ReactNode;
  tone?: 'danger' | 'default';
  confirmLabel?: string;
  requireTyping?: string;
  onConfirm?: () => void;
}

// 详情抽屉打开状态：聚焦用户 + 可选初始 tab
interface DrawerState {
  user: UserRow;
  tab?: string;
}

export default function UsersPage() {
  const { data: apps } = useApps();
  const { data: dashboard } = useDashboard();
  // 顶部 KPI 卡片接真（ADM-USR-S001，平台级全局口径）：累计/活跃/新增(30 天)取 GET /users/stats；
  // 封禁/风控取列表 status 筛选的真实全量 total（非首页 100 派生，避免 >100 漏计，codex #115）。
  const { data: userStats } = useUserStats();
  // KPI 渲染：userStats 为 null（加载中 / 接口失败）→ 显「—」，不把故障伪装成真 0（codex #115 四轮）。
  const kpi = (n?: number) => (n === undefined ? '—' : n.toLocaleString());

  // 风控策略 root 专属（后端 GET/PUT /risk-rules 均限 root）：非 root 隐藏「风控策略」tab 与 RiskPolicy。
  // RBAC 取数与路由守卫 RequireRole 同源 —— auth-store 的 user.role（前端只做 UX 隐藏，后端 requireRole 才是权威）。
  // useRiskRules() 不在此顶层调用：否则非 root 进 Users 也会发 GET /api/v1/risk-rules → 后端 403 重试。
  // 取数下沉到 root-only 的 RiskPolicyTab / RiskPolicy 内部（仅 isRoot 时挂载），非 root 根本不发该请求。
  const isRoot = useAuthStore((s) => s.user?.role === 'root');
  // 写动作前端 RBAC 门控（adversarial F4）：ban/unban=root/admin、grantBonus=root（后端 requireRole 为权威）
  const canModerate = useCanModerateUsers();
  const canGrantBonus = useCanGrantBonus();

  // 头部 KPI 迷你图：累计用户卡片的趋势线用真实近 30 天新增序列（dashboard.users30d）作形状指示，
  // 不再叠加原型的 +350000 假基线（否则曲线 ~35 万、与卡片真实累计数严重矛盾）。
  const usersTrendBase = dashboard.users30d;

  // 设备白名单（ADM-USR-S005，张培 PR332 迁顶层 + per-App）：后端已放宽 viewer+/operator+；web UI 入口暂保持
  // root（本页 tab/入口沿用 isRoot gate，operator+ 可视化列为独立 follow-up）；容器计数取全 App。
  const { data: deviceRows } = useDeviceWhitelist(undefined, isRoot);

  const queryClient = useQueryClient();
  // 解禁（ADM-USR-S003）：POST /api/v1/users/:id/unban → 服务端真值 toast + invalidate ['users']
  const unbanMutation = useMutation({
    networkMode: 'always',
    retry: false,
    mutationFn: (u: UserRow) => {
      if (!onlineManager.isOnline()) throw new Error('当前离线，请联网后重新确认操作');
      return usersApi.unban(u.id);
    },
    onSuccess: (dto) => {
      toast(`已解禁 ${dto.display_name ?? dto.email ?? dto.id}`, {
        tone: 'pos',
        icon: 'IconCheck',
      });
      queryClient.invalidateQueries({ queryKey: ['users'] });
      // 同时刷新顶部 KPI 封禁/风控计数（user-stats 独立缓存，否则解禁后卡片计数 stale，codex #115 三轮）
      queryClient.invalidateQueries({ queryKey: ['user-stats'] });
      // 解禁后关掉可能开着的用户详情抽屉（绑定的是解禁前快照，codex #54 bot）
      setDrawer(null);
    },
    onError: (err, u) => {
      toast(`${u.name} · ${err instanceof Error ? err.message : '解禁失败'}`, {
        tone: 'neg',
        icon: 'IconAlert',
      });
    },
  });

  const confirmUnban = (user: UserRow) => {
    if (!onlineManager.isOnline()) {
      toast('当前离线，请联网后重新确认操作', { tone: 'neg' });
      return;
    }
    unbanMutation.mutate(user);
  };

  const [tab, setTab] = useState('all');
  const [drawer, setDrawer] = useState<DrawerState | null>(null);
  const [q, setQ] = useState('');
  const [appFilter, setAppFilter] = useState<string[]>([]);
  const [countryFilter, setCountryFilter] = useState<string[]>([]);
  const [langFilter, setLangFilter] = useState<string[]>([]);
  const [planFilter, setPlanFilter] = useState<string[]>([]);
  const [tagFilter, setTagFilter] = useState<string[]>([]);
  const [paidFilter, setPaidFilter] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const pageSize = useAppScope((s) => s.tablePageSize);
  const userStatusFilter = tab === 'banned' ? 'banned' : tab === 'risk' ? 'risk' : undefined;
  // App 筛选下推（P1 回归修复）：后端 app_id 是**单值** uuid。appFilter 选 1 个 → 下推 app_id 服务端筛；
  // 选 0 个 = 不限 App；选 ≥2 个时后端无法表达多 App → 不下推、退回客户端按 appFilter 筛当前页（见 list）。
  const appIdForServer = appFilter.length === 1 ? appFilter[0] : null;
  // 付费状态：多选控件只选 1 项才下推（paid / unpaid）；全选 / 全不选 = 不限。
  const paidStatusForServer =
    paidFilter.length === 1 ? (paidFilter[0] === 'paid' ? 'paid' : 'unpaid') : undefined;
  // q / country / language 服务端下推：q 关键字 + country 多选（ISO alpha-2）+ language 当前内容语言多选（A3 §5.1），
  // 数组直接传。plan 后端未支持 → 不传（仍客户端筛）。
  const {
    data: users,
    total: usersTotal,
    isLoading: usersLoading,
  } = useUsersPage({
    page,
    pageSize,
    status: userStatusFilter,
    appId: appIdForServer,
    filters: {
      // q.trim()：空白串前端即视为「无筛选」，避免发出后端 .trim().min(1) 会 422 的纯空白 q（与 BooksPage 一致）
      ...(q.trim() ? { q: q.trim() } : {}),
      ...(countryFilter.length > 0 ? { country: countryFilter } : {}),
      ...(langFilter.length > 0 ? { language: langFilter } : {}),
      ...(tagFilter.length > 0 ? { tag_ids: tagFilter } : {}),
      ...(paidStatusForServer ? { paid_status: paidStatusForServer } : {}),
    },
  });
  const { data: userTags } = useUserTagsPage({ page: 1, pageSize: 200 });
  // 语言筛选候选 = 全部系统语言行减 zh / zh-*，覆盖 users.last_seen_language 的全值域（⊆ allowed_languages ∪ {en}）。
  // 不按 enabled 过滤：系统语言停用后存量 App 的 allowed_languages 保留它、C 端照常写入（70-产品配置 §0.4），
  // 按 enabled 过滤会让列表里看得到的语言在筛选里选不到。zh 及其变体禁入 allowed_languages，选了恒为空结果。
  // 筛选已下推服务端，不从当前页行派生（否则选中后候选只剩已选项）；已选码补回候选，保证可取消。
  const { data: sysConfig } = useSysConfig();
  const langFilterOptions = (() => {
    const codes = sysConfig.languages
      .map((l) => l.language_code)
      .filter((code) => code !== 'zh' && !code.startsWith('zh-'));
    for (const code of langFilter) if (!codes.includes(code)) codes.push(code);
    return codes.map((code) => ({ value: code, label: fmtLang(code) }));
  })();
  // 批量选择态：按内部 UUID 保留已选行快照，翻页后导出/批量 BONUS 仍包含此前页的用户。
  const [selectedUsers, setSelectedUsers] = useState<Map<string, UserRow>>(() => new Map());
  const selectedIds = new Set(selectedUsers.keys());

  // 列表头「导出」：GET /api/v1/users/export（与列表同筛选，裸 CSV 流）→ downloadAuthedCsv
  const exportMutation = useMutation({
    mutationFn: () =>
      usersApi.exportCsv({
        ...(appIdForServer ? { app_id: appIdForServer } : {}),
        ...(userStatusFilter ? { status: userStatusFilter } : {}),
        ...(q.trim() ? { q: q.trim() } : {}),
        ...(countryFilter.length > 0 ? { country: countryFilter } : {}),
        ...(langFilter.length > 0 ? { language: langFilter } : {}),
        ...(paidStatusForServer ? { paid_status: paidStatusForServer } : {}),
      }),
    onSuccess: ({ truncated }) =>
      toast(truncated ? '已导出（超上限仅导前 5000 行）' : '用户列表已导出', {
        tone: truncated ? 'warn' : 'pos',
        icon: 'IconDownload',
      }),
    onError: (err) =>
      toast(err instanceof Error ? err.message : '导出失败', { tone: 'neg', icon: 'IconAlert' }),
  });

  // 导出所选（客户端）：选区为跨页已加载行快照，服务端 filter 端点不表达任意 id → 客户端生成 CSV
  // 下载（只读、非编造数据）。CSV 单元格做公式注入中和 + 逗号/引号转义（同 server lib/csv 口径）。
  function exportSelectedRows() {
    const rows = [...selectedUsers.values()];
    if (rows.length === 0) {
      toast('未选择用户', { tone: 'warn' });
      return;
    }
    const cell = (v: string | number) => {
      let s = String(v ?? '');
      if (/^[=+\-@\t\r]/.test(s)) s = `'${s}`;
      return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const header = [
      'user_id',
      'display_user_id',
      'display_name',
      'email',
      'app',
      'country',
      'language',
      'status',
      'coin',
      'bonus',
    ];
    const lines = [header.join(',')];
    for (const u of rows) {
      lines.push(
        [
          u.id,
          u.displayUserId,
          u.name,
          u.email,
          u.appName,
          u.country,
          u.lang,
          u.status,
          u.coin,
          u.bonus,
        ]
          .map(cell)
          .join(','),
      );
    }
    // 前置 UTF-8 BOM（Excel 旧版中文不乱码）；Blob 下载
    const blob = new Blob([`﻿${lines.join('\n')}\n`], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `users-selected-${rows.length}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast(`已导出所选 ${rows.length} 个用户`, { tone: 'pos', icon: 'IconDownload' });
  }

  // 本页弹窗受控状态（替代原型全局 window.openDialog hub）
  const [whitelistOpen, setWhitelistOpen] = useState(false);
  const [deviceSanctionsOpen, setDeviceSanctionsOpen] = useState(false);
  const [banTarget, setBanTarget] = useState<UserRow | null>(null);
  const [grantTarget, setGrantTarget] = useState<UserRow | null>(null);
  // 批量发放 BONUS（多选工具条）：root-only
  const [batchGrantOpen, setBatchGrantOpen] = useState(false);
  const [confirm, setConfirm] = useState<ConfirmState | null>(null);

  // whitelist tab 不再走用户行过滤：契约无 device↔user 关联（白名单为设备维度），
  // 该 tab 渲染真实设备明细（WhitelistTabContent），原型的 isWhitelistUser 伪过滤删除。
  //
  // 服务端下推后（P1 回归修复）：status（tab）/ q / country / language / 单选 app_id 已由后端筛 → 这里**不再**重复客户端筛。
  // 仅保留两类客户端筛：
  //  - plan：后端不支持 + UserListItem 无真实 plan 数据（占位）→ 维持原型的客户端筛（pre-existing 非功能，不扩大范围）；
  //  - 多选 App（appFilter ≥2）：后端 app_id 单值无法表达 → 不下推时退回客户端按当前页筛。
  const isMultiAppClientFilter = appFilter.length > 1;
  const list = users.filter((u) => {
    if (isMultiAppClientFilter && !appFilter.includes(u.appId)) return false;
    if (planFilter.length && !planFilter.includes(u.plan)) return false;
    return true;
  });

  // 任一筛选 / tab / 本地分页偏好变化时回到第 1 页（依赖项即触发源）
  // biome-ignore lint/correctness/useExhaustiveDependencies: 该 effect 专门用依赖列表表达「哪些输入变化需要重置分页/选择」
  useEffect(() => {
    setPage(1);
    // 切 tab / 改筛选时清空批量选择（跨筛选保留选中会让「已选 N」语义错乱）
    setSelectedUsers(new Map());
  }, [tab, q, pageSize, appFilter, countryFilter, langFilter, planFilter, tagFilter, paidFilter]);

  // 分页 total 永远用服务端 usersTotal（status/q/country/language/单选app 已下推 → total 即筛选后的真实全量）。
  // 不再用 list.length 覆盖（那只是当前页计数，会把分页器与 total 误导成单页规模 —— P1 + 误导性 total）。
  // 例外：多选 App / plan 仍客户端筛当前页 —— 这两类下 total 仅为「服务端筛选后全量」的近似上界，
  // 属 pre-existing 非功能筛（plan 无真实数据；多选 App 后端单值无法表达），不为其重算 total。
  const paginationTotal = usersTotal;
  const pageList = list;

  // 表头全选：本页全部已选才算「全选」；toggle 时按本页 id 增删（不影响其它页已选）。
  // 普通选择与导出不限人数；无幂等单发端点的 50 人限制只在批量 BONUS 写入口执行。
  const allOnPageSelected = pageList.length > 0 && pageList.every((u) => selectedIds.has(u.id));
  const requestBatchGrantBonus = () => {
    if (selectedUsers.size <= MAX_BATCH_BONUS_SELECTED_USERS) {
      setBatchGrantOpen(true);
      return;
    }
    toast(`最多选择 ${MAX_BATCH_BONUS_SELECTED_USERS} 个用户，请先取消部分选择`, {
      tone: 'warn',
      icon: 'IconAlert',
    });
  };
  const toggleAllOnPage = () => {
    setSelectedUsers((prev) => {
      const next = new Map(prev);
      const allSelectedNow = pageList.length > 0 && pageList.every((user) => next.has(user.id));
      if (allSelectedNow) {
        for (const user of pageList) next.delete(user.id);
        return next;
      }
      const missingUsers = pageList.filter((user) => !next.has(user.id));
      for (const user of missingUsers) next.set(user.id, user);
      return next;
    });
  };
  const toggleOne = (user: UserRow) => {
    setSelectedUsers((prev) => {
      const next = new Map(prev);
      if (next.has(user.id)) next.delete(user.id);
      else next.set(user.id, user);
      return next;
    });
  };

  // 加载态只 gate「用户列表」tab 的表格区，**不**整页骨架（否则页头 / 设备白名单入口 / root-only
  // 风控策略 tab 这些走**独立查询**的能力会被 GET /users 一并锁死 —— 列表查询慢 / 挂起时操作员被困、
  // 退不出也切不了 tab，codex P2）。门控真实 TanStack Query isLoading（非原型假定时器）：
  // 页头 + KPI（各自 null/「—」兜底）+ tab 栏始终渲染、即时可交互；白名单 / 风控策略 tab 用各自查询的
  // loading；仅在用户列表 tab 内把表格行换成 TableSkeleton。

  return (
    <div className="page page-enter">
      <div className="page-header">
        <div>
          <h1 className="page-title">用户管理</h1>
          <div className="page-sub">
            累计 {kpi(userStats?.total_users)} · 活跃 (30 天) {kpi(userStats?.active_users)} · 新增
            (30 天) {kpi(userStats?.new_users)} · 封禁 {kpi(userStats?.banned_users)}
          </div>
        </div>
        <div className="hstack" style={{ gap: 8 }}>
          <button className="btn" onClick={() => setDeviceSanctionsOpen(true)}>
            设备处分
          </button>
          {/* 设备白名单 root 专属（后端全部端点 requireRole('root')）：非 root 隐藏入口 ——
              否则 WhitelistDialog 内 useDeviceWhitelist()（enabled=true）会发 403 GET，
              且降权会话可能读到 root 之前的 ['device-whitelist'] 缓存行（机密泄漏，adversarial F1）*/}
          {isRoot && (
            <button className="btn" onClick={() => setWhitelistOpen(true)}>
              <Icons.IconShield size={13} />
              设备白名单
            </button>
          )}
          {/* 导出用户 CSV（接真）：GET /api/v1/users/export，与列表同筛选（app_id/status/q/country）→ downloadAuthedCsv */}
          <button
            className="btn"
            disabled={exportMutation.isPending}
            onClick={() => exportMutation.mutate()}
          >
            <Icons.IconDownload size={13} />
            {exportMutation.isPending ? '导出中…' : '导出'}
          </button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
        <div className="card" style={{ padding: 14 }}>
          <div className="stat-label">累计用户</div>
          <div className="stat-value" style={{ fontSize: 22, marginTop: 4 }}>
            {kpi(userStats?.total_users)}
          </div>
          <Charts.Sparkline data={usersTrendBase} width={200} height={26} color="var(--chart-2)" />
        </div>
        <div className="card" style={{ padding: 14 }}>
          <div className="stat-label">活跃 (30 天)</div>
          <div className="stat-value" style={{ fontSize: 22, marginTop: 4 }}>
            {kpi(userStats?.active_users)}
          </div>
          <Charts.Sparkline
            data={dashboard.dau30d}
            width={200}
            height={26}
            color="var(--chart-3)"
          />
        </div>
        <div className="card" style={{ padding: 14 }}>
          <div className="stat-label">新增 (30 天)</div>
          <div className="stat-value" style={{ fontSize: 22, marginTop: 4 }}>
            {kpi(userStats?.new_users)}
          </div>
          <Charts.Sparkline
            data={dashboard.users30d}
            width={200}
            height={26}
            color="var(--chart-1)"
          />
        </div>
        <div className="card" style={{ padding: 14 }}>
          <div className="stat-label">封禁 / 风控</div>
          <div className="stat-value" style={{ fontSize: 22, marginTop: 4 }}>
            {kpi(userStats?.banned_users)}{' '}
            <span className="muted" style={{ fontSize: 11 }}>
              / {kpi(userStats?.risk_users)} 观察
            </span>
          </div>
          <div className="hstack" style={{ gap: 4, marginTop: 8 }}>
            {Array.from({ length: 30 }).map((_, i) => (
              <span
                key={i}
                style={{
                  flex: 1,
                  height: 18,
                  borderRadius: 2,
                  background: i < 12 ? 'var(--neg)' : i < 18 ? 'var(--warn)' : 'var(--bg-muted)',
                }}
              />
            ))}
          </div>
        </div>
      </div>

      <div style={{ borderBottom: '1px solid var(--border)', display: 'flex' }}>
        {[
          // tab 徽章用平台级真实全量计数（useUserStats，与顶部 KPI 同口径），不再用当前页 users 派生
          // （服务端分页后 users 只是当前页 → 用它做计数会严重低报，且随翻页/筛选漂移 —— 误导性 total）。
          // userStats=null（加载中 / 失败）→ 退回服务端 usersTotal（当前 status 筛选的全量；'all' tab 即全部用户）。
          { id: 'all', label: '全部用户', n: (userStats?.total_users ?? usersTotal) as ReactNode },
          { id: 'risk', label: '风控观察', n: userStats?.risk_users ?? usersTotal },
          { id: 'banned', label: '已封禁', n: userStats?.banned_users ?? usersTotal },
          // 白名单计数 = 真实设备数（root 取数）；非 root 不发请求 → 显「—」（不显误导性 0）
          { id: 'whitelist', label: '设备白名单', n: isRoot ? deviceRows.length : '—' },
          // 标签定义全局一份、无 App 维度，清单已在本页取数 → 直接用条数，不显无信息量的「—」
          { id: 'tags', label: '用户标签', n: userTags.length },
        ].map((t) => (
          <TabButton key={t.id} id={t.id} label={t.label} n={t.n} active={tab} onSelect={setTab} />
        ))}
        {/* 风控策略 root 专属 —— 仅 isRoot 时挂载，内部 useRiskRules() 取启用规则数；非 root 不渲染、不发请求 */}
        {isRoot && <RiskPolicyTab active={tab} onSelect={setTab} />}
      </div>

      {tab === 'policy' && isRoot ? (
        <RiskPolicy />
      ) : tab === 'tags' ? (
        <UserTagsTab
          onFilterUsers={(id) => {
            setTagFilter([id]);
            setTab('all');
          }}
        />
      ) : tab === 'whitelist' ? (
        <WhitelistTabContent
          rows={deviceRows}
          isRoot={isRoot}
          onOpenDialog={() => setWhitelistOpen(true)}
        />
      ) : (
        <>
          <div className="hstack" style={{ gap: 8 }}>
            <SearchInput
              width={280}
              placeholder="用户名 / 邮箱 / 内部 ID / 展示用户 ID"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            <FilterButton
              label="App"
              value={appFilter}
              onChange={setAppFilter}
              options={apps.map((a) => ({ value: a.id, label: a.name }))}
              width={180}
            />
            <FilterButton
              label="地区"
              value={countryFilter}
              onChange={setCountryFilter}
              options={[...new Set(users.map((u) => u.country))]
                .sort()
                .map((c) => ({ value: c, label: fmtCountry(c) }))}
              width={200}
              searchable
            />
            <FilterButton
              label="语言"
              value={langFilter}
              onChange={setLangFilter}
              options={langFilterOptions}
              width={180}
              searchable
            />
            <FilterButton
              label="订阅"
              value={planFilter}
              onChange={setPlanFilter}
              options={[
                { value: '免费', label: '免费' },
                { value: 'Plus', label: 'Plus' },
                { value: 'Premium', label: 'Premium' },
              ]}
              width={150}
            />
            <FilterButton
              label="付费"
              value={paidFilter}
              onChange={setPaidFilter}
              options={[
                { value: 'paid', label: '付费用户' },
                { value: 'unpaid', label: '未付费用户' },
              ]}
              width={150}
            />
            <FilterButton
              label="用户标签"
              value={tagFilter}
              onChange={setTagFilter}
              options={userTags
                .filter((t) => t.binding_status === 'bound' && t.is_active)
                .map((t) => ({ value: t.id, label: t.name, hint: t.tag_key }))}
              width={240}
              searchable
              // 人群筛选只收「已绑定 ∧ is_active=true」的标签，注册表为空时候选必然空；
              // 默认「无结果」会被当成加载失败，这里说清为什么空、去哪看
              emptyLabel={
                userTags.length === 0
                  ? '还没有用户标签 · 去「用户标签」页新建'
                  : '无可用于筛选的标签 · 仅「已绑定 ∧ 已启用」的标签可入人群筛选，详见「用户标签」页'
              }
            />
            <div style={{ flex: 1 }} />
            <span className="mono" style={{ fontSize: 11, color: 'var(--fg-muted)' }}>
              共 {usersTotal} 项
            </span>
          </div>

          {selectedIds.size > 0 && (
            <div
              className="hstack"
              style={{
                gap: 10,
                padding: '8px 12px',
                borderRadius: 8,
                background: 'var(--accent-soft)',
                border: '1px solid var(--accent)',
              }}
            >
              <span style={{ fontSize: 12.5, fontWeight: 500, color: 'var(--accent-fg)' }}>
                已选 {selectedIds.size} 个用户
              </span>
              <div style={{ flex: 1 }} />
              {/* 批量发放 BONUS：前端以有限并发（5-worker）编排单发端点 → root-only（后端 requireRole 权威）*/}
              <button
                className="btn btn-sm"
                disabled={!canGrantBonus}
                title={canGrantBonus ? undefined : FORBIDDEN_ROLE_TITLE}
                style={canGrantBonus ? undefined : { opacity: 0.55, cursor: 'not-allowed' }}
                onClick={requestBatchGrantBonus}
              >
                <Icons.IconCoin size={11} />
                批量发放 BONUS
              </button>
              {/* 导出所选 CSV（接真）：选区是跨页已加载行快照（服务端 filter 端点不表达任意 id）→ 客户端导出
                  （只读下载、非编造数据，与服务端「导出全部筛选」互补）*/}
              <button className="btn btn-sm" onClick={() => exportSelectedRows()}>
                <Icons.IconDownload size={11} />
                导出所选
              </button>
              <button className="btn btn-sm" onClick={() => setSelectedUsers(new Map())}>
                清除选择
              </button>
            </div>
          )}

          {usersLoading ? (
            // 仅列表区骨架（13 列含勾选列）：页头 / 筛选 / tab 栏已在上方照常渲染、可交互，
            // 列表查询慢 / 挂起时操作员仍能切 tab / 走白名单入口，不被整页骨架困住（codex P2）
            <TableSkeleton rows={11} cols={12} checkbox />
          ) : (
            <div className="card" style={{ overflow: 'hidden' }}>
              <Table className="tbl">
                <thead>
                  <tr>
                    <th style={{ width: 32 }}>
                      <input
                        type="checkbox"
                        checked={allOnPageSelected}
                        onChange={toggleAllOnPage}
                        title="选中本页全部"
                      />
                    </th>
                    <th>用户</th>
                    <th>App</th>
                    <th>地区</th>
                    <th>语言</th>
                    <th className="num">COIN</th>
                    <th className="num">BONUS</th>
                    <th className="num">累计充值</th>
                    <th>订阅</th>
                    <th>注册</th>
                    <th>最近</th>
                    <th>状态</th>
                    <th style={{ width: 32 }} />
                  </tr>
                </thead>
                <tbody>
                  {pageList.map((u) => (
                    <tr
                      key={u.id}
                      onClick={() => setDrawer({ user: u })}
                      style={{ cursor: 'pointer' }}
                    >
                      <td onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedIds.has(u.id)}
                          onChange={() => toggleOne(u)}
                        />
                      </td>
                      <td>
                        <div className="hstack" style={{ gap: 8 }}>
                          <Avatar seed={u.id} size={24} fontSize={10}>
                            {u.name[0]}
                          </Avatar>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontSize: 12.5, fontWeight: 500 }}>{u.name}</div>
                            <div
                              className="mono"
                              style={{ fontSize: 10, color: 'var(--fg-faint)' }}
                              title={`展示用户 ID: ${formatDisplayUserIdForPresentation(u.displayUserId)}`}
                            >
                              展示 · {formatDisplayUserIdForPresentation(u.displayUserId)}
                            </div>
                            <div
                              className="mono"
                              style={{ fontSize: 10, color: 'var(--fg-faint)' }}
                              title={`内部用户 ID: ${u.id}`}
                            >
                              内部 · {u.id}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className="chip" style={{ fontSize: 10 }}>
                          {u.appName}
                        </span>
                      </td>
                      <td style={{ fontSize: 11 }}>{fmtCountry(u.country)}</td>
                      {/* 当前内容语言（A3 §5.2）：空串 = 从未上报 / 旧响应缺键 → '—'，不断言具体状态 */}
                      <td style={{ fontSize: 11 }}>
                        {u.lang ? (
                          <>
                            <span className="mono" style={{ fontSize: 10.5 }}>
                              {u.lang.toUpperCase()}
                            </span>
                            <span style={{ color: 'var(--fg-faint)', marginLeft: 5 }}>
                              {langName(u.lang)}
                            </span>
                          </>
                        ) : (
                          <span style={{ color: 'var(--fg-faint)' }}>—</span>
                        )}
                      </td>
                      <td className="num">{u.coin.toLocaleString()}</td>
                      <td className="num">{u.bonus.toLocaleString()}</td>
                      {/* 累计充值 per-currency（catch-up #130）：adapter 已含币种符号「€128.40 · zł45.00」/ '—'，直接渲染 */}
                      <td className="num">{u.totalSpend}</td>
                      <td>
                        <span
                          className={`chip ${u.plan === 'Premium' ? 'chip-accent' : u.plan === 'Plus' ? 'chip-info' : ''}`}
                          style={{ fontSize: 10 }}
                        >
                          {u.plan}
                        </span>
                      </td>
                      <td className="mono" style={{ fontSize: 10.5, color: 'var(--fg-muted)' }}>
                        {fmtDateTime(u.registeredAt)}
                      </td>
                      <td style={{ fontSize: 11, color: 'var(--fg-muted)' }}>
                        {fmtDateTime(u.lastSeen)}
                      </td>
                      <td>
                        <span
                          className={`chip ${u.status === '正常' ? 'chip-pos' : u.status === '已封禁' ? 'chip-neg' : 'chip-warn'}`}
                          style={{ fontSize: 10 }}
                        >
                          ● {u.status}
                        </span>
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <ContextMenu
                          items={[
                            {
                              label: '查看用户',
                              icon: 'IconEye',
                              onClick: () => setDrawer({ user: u }),
                            },
                            {
                              label: '复制展示用户 ID',
                              icon: 'IconCopy',
                              disabled: !hasDisplayUserId(u.displayUserId),
                              title: hasDisplayUserId(u.displayUserId)
                                ? undefined
                                : '展示用户 ID 不可用',
                              onClick: () =>
                                void copyUserIdToClipboard(u.displayUserId, '展示用户 ID'),
                            },
                            {
                              label: '复制内部用户 ID',
                              icon: 'IconCopy',
                              onClick: () => void copyUserIdToClipboard(u.id, '内部用户 ID'),
                            },
                            '---',
                            { section: '运营' },
                            {
                              // 发放 BONUS：root 专属（后端 grant-bonus requireRole('root')）→ 非 root 禁用入口（F4）
                              label: '发放 BONUS',
                              icon: 'IconCoin',
                              disabled: !canGrantBonus,
                              title: canGrantBonus ? undefined : FORBIDDEN_ROLE_TITLE,
                              onClick: () => setGrantTarget(u),
                            },
                            {
                              // 站内私信：契约无端点（NOT-in-scope，计划 §五）→ 菜单项禁用
                              label: '发送消息',
                              icon: 'IconMail',
                              disabled: true,
                              onClick: () => {},
                            },
                            {
                              label: '查看反馈记录',
                              icon: 'IconMsg',
                              onClick: () => setDrawer({ user: u, tab: 'feedback' }),
                            },
                            '---',
                            { section: '风控' },
                            ...(u.status === '已封禁'
                              ? [
                                  {
                                    // 解禁账户：root/admin（后端 unban requireRole）→ 其它角色禁用入口（F4）
                                    label: '解禁账户',
                                    icon: 'IconCheck' as const,
                                    disabled: !canModerate,
                                    title: canModerate ? undefined : FORBIDDEN_ROLE_TITLE,
                                    onClick: () =>
                                      setConfirm({
                                        title: `解禁 ${u.name}？`,
                                        message:
                                          '解禁后用户可立即恢复登录使用，本次解禁将记录到操作日志。',
                                        tone: 'default',
                                        confirmLabel: '确认解禁',
                                        // 真实接线：POST /users/:id/unban（成功 toast / 失败报错在 mutation 内）
                                        onConfirm: () => confirmUnban(u),
                                      }),
                                  },
                                ]
                              : [
                                  // 加入设备白名单：契约无 user↔device 关联（拿不到用户主设备 device_id）
                                  // → 打开白名单弹窗手动录入 device_id（真实 addDevice 路径）；端点 root 专属，
                                  // 非 root 不展示（与风控策略 tab 同一套 RBAC UX 收敛，后端 requireRole 为权威）
                                  ...(isRoot
                                    ? [
                                        {
                                          label: '加入设备白名单',
                                          icon: 'IconShield' as const,
                                          onClick: () => setWhitelistOpen(true),
                                        },
                                      ]
                                    : []),
                                  {
                                    // 封禁账户：root/admin（后端 ban requireRole）→ 其它角色禁用入口（F4）
                                    label: '封禁账户',
                                    icon: 'IconBan' as const,
                                    tone: 'danger' as const,
                                    disabled: !canModerate,
                                    title: canModerate ? undefined : FORBIDDEN_ROLE_TITLE,
                                    onClick: () => setBanTarget(u),
                                  },
                                ]),
                            '---',
                            {
                              // 删除账户：契约无端点（GDPR 链路需先定契约，NOT-in-scope）→ 菜单项禁用
                              label: '删除账户',
                              icon: 'IconTrash',
                              tone: 'danger',
                              disabled: true,
                              onClick: () => {},
                            },
                          ]}
                        >
                          <button
                            className="btn btn-ghost btn-icon"
                            style={{ width: 22, height: 22 }}
                          >
                            <Icons.IconMoreV size={12} />
                          </button>
                        </ContextMenu>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              <Pagination
                total={paginationTotal}
                page={page}
                pageSize={pageSize}
                onChange={setPage}
              />
            </div>
          )}
        </>
      )}

      {drawer && (
        <UserDetail
          key={drawer.user.id}
          user={drawer.user}
          initialTab={drawer.tab}
          isRoot={isRoot}
          canModerate={canModerate}
          canGrantBonus={canGrantBonus}
          onClose={() => setDrawer(null)}
          onGrantBonus={(u) => setGrantTarget(u)}
          onBan={(u) => setBanTarget(u)}
          onUnban={confirmUnban}
          onOpenWhitelist={() => setWhitelistOpen(true)}
          onShowPolicy={() => {
            setDrawer(null);
            setTab('policy');
          }}
          onConfirm={(c) => setConfirm(c)}
        />
      )}

      {whitelistOpen && <WhitelistDialog onClose={() => setWhitelistOpen(false)} />}
      {deviceSanctionsOpen && (
        <DeviceSanctionsDialog onClose={() => setDeviceSanctionsOpen(false)} />
      )}
      {banTarget && (
        <BanUserDialog
          user={banTarget}
          onClose={() => setBanTarget(null)}
          onBanned={() => setDrawer(null)}
        />
      )}
      {grantTarget && <GrantBonusDialog user={grantTarget} onClose={() => setGrantTarget(null)} />}
      {batchGrantOpen && (
        <BatchGrantBonusDialog
          users={[...selectedUsers.values()]}
          onClose={() => setBatchGrantOpen(false)}
          onSelectionReconciled={(safeToRetryUserIds) =>
            setSelectedUsers((prev) => {
              const next = new Map<string, UserRow>();
              for (const userId of safeToRetryUserIds) {
                const user = prev.get(userId);
                if (user) next.set(userId, user);
              }
              return next;
            })
          }
        />
      )}
      {confirm && (
        <ConfirmDialog
          title={confirm.title}
          message={confirm.message}
          tone={confirm.tone}
          confirmLabel={confirm.confirmLabel}
          requireTyping={confirm.requireTyping}
          onConfirm={() => confirm.onConfirm?.()}
          onClose={() => setConfirm(null)}
        />
      )}
    </div>
  );
}

// 用户 tab 栏单个按钮（抽出以便 root-only 的 RiskPolicyTab 复用同款样式，不重复 markup）
function TabButton({
  id,
  label,
  n,
  active,
  onSelect,
}: {
  id: string;
  label: string;
  n: ReactNode;
  active: string;
  onSelect: (id: string) => void;
}) {
  return (
    <button
      onClick={() => onSelect(id)}
      style={{
        border: 0,
        background: 'transparent',
        padding: '9px 14px',
        font: 'inherit',
        fontSize: 12.5,
        cursor: 'pointer',
        color: active === id ? 'var(--fg-strong)' : 'var(--fg-muted)',
        fontWeight: active === id ? 500 : 400,
        borderBottom: active === id ? '2px solid var(--accent)' : '2px solid transparent',
        marginBottom: -1,
      }}
    >
      {label}{' '}
      <span className="mono" style={{ color: 'var(--fg-faint)', fontSize: 11 }}>
        · {n}
      </span>
    </button>
  );
}

// 风控策略 tab 按钮（root-only）：在此组件内部取数算启用规则数，仅 isRoot 时挂载 →
// 把 GET /api/v1/risk-rules 收敛进 root-only 子树，非 root 永不触发该请求（后端限 root，否则 403 重试）。
function RiskPolicyTab({ active, onSelect }: { active: string; onSelect: (id: string) => void }) {
  const { data: riskRules } = useRiskRules();
  // 风控策略 tab 计数：启用规则数（原型 D.RISK_RULES.filter(r => r.enabled).length）
  const riskEnabledCount = riskRules.filter((r) => r.enabled).length;
  return (
    <TabButton
      id="policy"
      label="风控策略"
      n={riskEnabledCount}
      active={active}
      onSelect={onSelect}
    />
  );
}

// ADM-USR-S007 · 风控策略配置（超管专属 · 机密）
function RiskPolicy() {
  const appId = useAppScope((s) => s.appId);
  const {
    data: riskRules,
    isError: riskRulesUnavailable,
    isLoading: riskRulesLoading,
    isFetching: riskRulesRefreshing,
    isServiceUpdating,
    refetch: reloadRiskRules,
  } = useRiskRules();
  const [writeUnavailableScope, setWriteUnavailableScope] = useState<string | null>(null);
  const riskStateUnconfirmed = riskRulesLoading || (appId !== 'all' && riskRulesRefreshing);
  const appServiceUpdating =
    isServiceUpdating || (appId !== 'all' && writeUnavailableScope === appId);
  const queryClient = useQueryClient();
  const [rules, setRules] = useState<RiskRuleRow[]>(riskRules);
  // 阈值编辑弹窗目标行（design dialogs3.jsx risk-rule-edit）
  const [editTarget, setEditTarget] = useState<RiskRuleRow | null>(null);
  // 失败回滚计数：共享 Switch 点击先自翻视觉态，PATCH 失败时行数据未变、
  // 仅靠 prop 同步无法复位 —— bump nonce 触发 Switch 重挂回服务端真值（PR1 模式）
  const [switchResetNonce, setSwitchResetNonce] = useState(0);

  // rows 始终镜像服务端真值（toggle 已走真 PATCH，本地不再手翻 enabled）
  useEffect(() => {
    setRules(riskRules);
  }, [riskRules]);

  // 启停 toggle（PATCH /api/v1/risk-rules/:id/enabled）—— 完全照 SystemConfig 范式对账（adversarial F2）：
  // onSuccess：adapt 返回 DTO → 同步 patch 本地 rules + setQueryData(['risk-rules']) 合并 → 再 await invalidate；
  // onError：回滚视觉态后 await invalidate refetch 对账（写可能已提交，不盲信旧态）。
  const toggleMutation = useMutation({
    networkMode: 'always',
    retry: false,
    mutationFn: ({ id, enabled, scope }: { id: string; enabled: boolean; scope: string }) => {
      if (!onlineManager.isOnline()) throw new Error('当前离线，请联网后重新确认操作');
      return riskRulesApi.setEnabled(id, enabled, scope === 'all' ? undefined : scope);
    },
    // async + await invalidate：isPending 覆盖到 refetch 落地，关掉「响应已回、列表未刷、
    // Switch 复活」的二次点击窗口（同 SystemConfig toggle）
    onSuccess: async (dto, vars) => {
      const adapted = adaptRiskRule(dto);
      const mergeToggleFields = (current: RiskRuleRow): RiskRuleRow => {
        if (current.id !== dto.id) return current;
        const currentTime = Date.parse(current.updatedAt);
        const ackTime = Date.parse(adapted.updatedAt);
        // global可能已有更晚阈值ACK；编辑人/时间同源保留，不能由迟到启停响应倒退。
        const keepMetadata =
          Number.isFinite(currentTime) && (!Number.isFinite(ackTime) || currentTime >= ackTime);
        return {
          ...current,
          enabled: adapted.enabled,
          ...(keepMetadata ? {} : { updatedBy: adapted.updatedBy, updatedAt: adapted.updatedAt }),
        };
      };
      // 启停ACK只持有enabled及相应编辑元数据；阈值、条件、统计始终保留当前确认值。
      if (useAppScope.getState().appId === vars.scope) {
        setRules((prev) => prev.map(mergeToggleFields));
      }
      queryClient.setQueryData<RiskRuleRow[]>(['risk-rules', vars.scope], (old) =>
        old ? old.map(mergeToggleFields) : old,
      );
      toast(`已保存「${dto.name}」${vars.scope === 'all' ? '全局默认' : '当前 App'}开关`, {
        tone: dto.enabled ? 'pos' : 'warn',
        icon: 'IconShield',
      });
      await queryClient.invalidateQueries({ queryKey: ['risk-rules'] });
    },
    // 失败 ≠ 一定没写入（响应丢失/超时/解码失败都走这里）：回滚视觉态后必须 refetch 对账服务端真值
    onError: async (err, vars) => {
      setSwitchResetNonce((n) => n + 1);
      const appEndpointMissing =
        vars.scope !== 'all' && err instanceof AdminApiError && err.httpStatus === 404;
      if (appEndpointMissing) setWriteUnavailableScope(vars.scope);
      const name = rules.find((r) => r.id === vars.id)?.name ?? vars.id;
      toast(
        `${name} · ${appEndpointMissing ? '服务更新中，当前 App 风控开关未确认' : err instanceof Error ? err.message : '操作失败'}`,
        {
          tone: 'neg',
          icon: 'IconAlert',
        },
      );
      await queryClient.invalidateQueries({ queryKey: ['risk-rules'] });
    },
  });

  const high = rules.filter((r) => r.level === 'high' && r.enabled).length;
  const med = rules.filter((r) => r.level === 'medium' && r.enabled).length;
  const low = rules.filter((r) => r.level === 'low' && r.enabled).length;
  const totalHits = rules.reduce((s, r) => s + r.hits, 0);

  return (
    <div className="vstack" style={{ gap: 12 }}>
      <div
        className="card"
        style={{ padding: 14, background: 'var(--info-soft)', borderColor: 'transparent' }}
      >
        <div className="hstack" style={{ gap: 10, alignItems: 'flex-start' }}>
          <Icons.IconShield size={14} style={{ color: 'var(--info)', marginTop: 2 }} />
          <div style={{ flex: 1, fontSize: 12.5, lineHeight: 1.55 }}>
            <span style={{ fontWeight: 500 }}>ADM-USR-S007 · 风控策略</span>
            <span
              className="chip chip-neg"
              style={{ fontSize: 10, margin: '0 6px', verticalAlign: 'middle' }}
            >
              <Icons.IconLock size={9} />
              超管专属 · 机密
            </span>
            <span style={{ color: 'var(--fg-muted)' }}>
              {appId === 'all'
                ? '当前修改全局默认开关；切换顶栏 App 可设置独立开关。'
                : '当前仅修改所选 App 的开关；阈值仍为全局规则配置。'}
              {appId === 'all'
                ? '全局默认配置，不能据此判断任一 App 已生效。'
                : '当前 App 的有效启用状态。'}
              白名单豁免全部自动封禁规则，观察规则仍适用。
            </span>
            {(riskRulesUnavailable || appServiceUpdating) && (
              <div role="alert" style={{ color: 'var(--neg)', marginTop: 8 }}>
                {appServiceUpdating
                  ? '服务更新中，不能确认当前 App 的风控状态，App 启停暂不可用。'
                  : '风控状态读取失败，不能确认已生效。请检查服务与注册事实完整性。'}
                <button
                  className="btn btn-sm"
                  onClick={() => {
                    setWriteUnavailableScope(null);
                    reloadRiskRules();
                  }}
                >
                  重新加载
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12 }}>
        {[
          {
            label: '启用规则',
            v: rules.filter((r) => r.enabled).length,
            sub: `/ ${rules.length} 总计`,
          },
          { label: 'High 等级', v: high, tone: 'neg' },
          { label: 'Medium 等级', v: med, tone: 'warn' },
          { label: 'Low 等级', v: low },
          { label: '本月命中次数', v: totalHits.toLocaleString() },
        ].map((s) => (
          <div key={s.label} className="card" style={{ padding: 14 }}>
            <div className="stat-label">{s.label}</div>
            <div
              className="stat-value"
              style={{
                fontSize: 22,
                marginTop: 4,
                color:
                  s.tone === 'neg'
                    ? 'var(--neg)'
                    : s.tone === 'warn'
                      ? 'var(--warn)'
                      : 'var(--fg-strong)',
              }}
            >
              {s.v}
            </div>
            {s.sub && (
              <div
                className="mono"
                style={{ fontSize: 11, color: 'var(--fg-faint)', marginTop: 2 }}
              >
                {s.sub}
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="card" style={{ padding: '4px 18px 14px' }}>
        <div
          style={{
            padding: '12px 0',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 500, fontSize: 13.5 }}>风控规则清单</div>
            <div style={{ fontSize: 11.5, color: 'var(--fg-muted)', marginTop: 2 }}>
              启停后约 60 秒内生效 · 命中账户记入审计日志 · 阈值类规则仅可编辑 condition_value
              （条件维度 / 运算符只读）
            </div>
          </div>
          {/* 新建规则：契约明确本轮不开放（规则集由服务端维护）→ 禁用锁态，与设计稿一致 */}
          <button
            className="btn btn-sm"
            disabled
            title="本轮不开放新建规则 · 规则集由服务端维护"
            style={{ opacity: 0.55, cursor: 'not-allowed' }}
          >
            <Icons.IconPlus size={11} />
            新建规则
            <Icons.IconLock size={9} style={{ marginLeft: 4, color: 'var(--fg-faint)' }} />
          </button>
        </div>
        <Table className="tbl">
          <thead>
            <tr>
              <th style={{ width: 50 }}>启用</th>
              <th>规则名称</th>
              <th>条件表达式</th>
              <th>执行动作</th>
              <th>等级</th>
              <th className="num">命中数</th>
              <th>最近命中</th>
              <th>更新</th>
              <th style={{ width: 60 }} />
            </tr>
          </thead>
          <tbody>
            {rules.map((r) => (
              <tr key={r.id}>
                <td>
                  {/* 共享 Switch sm 档（30×17）与原型行内 toggle 逐像素一致；
                      失败时 bump nonce 重挂回滚自翻视觉态（PR1 模式） */}
                  <Switch
                    key={`${r.id}:${switchResetNonce}`}
                    size="sm"
                    on={r.enabled}
                    disabled={
                      toggleMutation.isPending ||
                      riskStateUnconfirmed ||
                      riskRulesUnavailable ||
                      appServiceUpdating
                    }
                    onChange={(next) => {
                      if (riskStateUnconfirmed || riskRulesUnavailable || appServiceUpdating) {
                        setSwitchResetNonce((value) => value + 1);
                        return;
                      }
                      if (!onlineManager.isOnline()) {
                        toast('当前离线，请联网后重新确认操作', { tone: 'neg' });
                        setSwitchResetNonce((value) => value + 1);
                        return;
                      }
                      toggleMutation.mutate({ id: r.id, enabled: next, scope: appId });
                    }}
                  />
                </td>
                <td>
                  <div style={{ fontWeight: 500, fontSize: 12.5 }}>{r.name}</div>
                  <div className="mono" style={{ fontSize: 10.5, color: 'var(--fg-faint)' }}>
                    {r.id}
                  </div>
                </td>
                <td>
                  <code
                    style={{
                      padding: '2px 6px',
                      borderRadius: 3,
                      background: 'var(--bg-subtle)',
                      fontSize: 11,
                      color: 'var(--fg-muted)',
                    }}
                  >
                    {r.condition}
                  </code>
                </td>
                <td>
                  {r.action === '自动封禁' ? (
                    <span className="chip chip-neg" style={{ fontSize: 10 }}>
                      <Icons.IconBan size={9} />
                      {r.action}
                    </span>
                  ) : (
                    <span className="chip chip-warn" style={{ fontSize: 10 }}>
                      <Icons.IconEye size={9} />
                      {r.action}
                    </span>
                  )}
                </td>
                <td>
                  <span
                    className={`chip ${r.level === 'high' ? 'chip-neg' : r.level === 'medium' ? 'chip-warn' : ''}`}
                    style={{ fontSize: 10 }}
                  >
                    {r.level === 'high' ? 'High' : r.level === 'medium' ? 'Medium' : 'Low'}
                  </span>
                </td>
                <td className="num">{r.hits.toLocaleString()}</td>
                <td className="mono" style={{ fontSize: 11, color: 'var(--fg-muted)' }}>
                  {fmtDateTime(r.lastHit) || '—'}
                </td>
                <td className="mono" style={{ fontSize: 11, color: 'var(--fg-muted)' }}>
                  {fmtDateTime(r.updatedAt)} by {r.updatedBy}
                </td>
                <td>
                  <div className="hstack" style={{ gap: 4 }}>
                    <button
                      className="btn btn-ghost btn-icon"
                      style={{ width: 22, height: 22 }}
                      title="编辑阈值 · 仅标量阈值 (condition_value) 可改"
                      onClick={() => setEditTarget(r)}
                    >
                      <Icons.IconEdit size={11} />
                    </button>
                    <button
                      className="btn btn-ghost btn-icon"
                      style={{ width: 22, height: 22 }}
                      title="查看命中历史"
                      onClick={() =>
                        toast(
                          `「${r.name}」近 30 天命中 ${r.hits.toLocaleString()} 次 · 明细待接入`,
                          {
                            tone: 'info',
                            icon: 'IconHistory',
                          },
                        )
                      }
                    >
                      <Icons.IconHistory size={11} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      <div className="card" style={{ padding: 14 }}>
        <div className="hstack" style={{ marginBottom: 8 }}>
          <div style={{ fontWeight: 500, fontSize: 13 }}>全局开关</div>
          <span className="mono muted" style={{ marginLeft: 6, fontSize: 11 }}>
            · 优先于单条规则
          </span>
        </div>
        <div className="vstack" style={{ gap: 8 }}>
          <div
            className="hstack"
            style={{
              gap: 10,
              padding: '8px 12px',
              border: '1px solid var(--border)',
              borderRadius: 6,
            }}
          >
            <Icons.IconShield size={13} style={{ color: 'var(--info)' }} />
            <span style={{ fontSize: 12, flex: 1 }}>全部风控规则总开关</span>
            <span className="chip" style={{ fontSize: 10 }}>
              未接入
            </span>
          </div>
          <div
            className="hstack"
            style={{
              gap: 10,
              padding: '8px 12px',
              border: '1px solid var(--border)',
              borderRadius: 6,
            }}
          >
            <Icons.IconAlert size={13} style={{ color: 'var(--warn)' }} />
            <span style={{ fontSize: 12, flex: 1 }}>
              呆账人工复核· high 等级规则默认直接执行，low 需人工确认
            </span>
            <span className="chip" style={{ fontSize: 10 }}>
              未接入
            </span>
          </div>
          <div
            className="hstack"
            style={{
              gap: 10,
              padding: '8px 12px',
              border: '1px solid var(--border)',
              borderRadius: 6,
            }}
          >
            <Icons.IconCheckCircle size={13} style={{ color: 'var(--pos)' }} />
            <span style={{ fontSize: 12, flex: 1 }}>
              设备白名单豁免全部自动封禁规则，观察仍计入命中；不解除既有处分。
            </span>
            <span className="chip" style={{ fontSize: 10 }}>
              未接入
            </span>
          </div>
        </div>
      </div>

      {editTarget && (
        <RiskRuleThresholdEditDialog rule={editTarget} onClose={() => setEditTarget(null)} />
      )}
    </div>
  );
}

// 设备白名单 tab 内容（ADM-USR-S005）：信息 banner + 真实设备明细。
// 契约白名单为设备维度、无 device↔user 关联 → 不再按原型「按关联用户展开」用户行
// （那是 mock 伪关联），改为直接渲染 device_whitelist 真实行；增删见右上弹窗。
function WhitelistTabContent({
  rows,
  isRoot,
  onOpenDialog,
}: {
  rows: DeviceWhitelistRow[];
  isRoot: boolean;
  onOpenDialog: () => void;
}) {
  return (
    <div className="vstack" style={{ gap: 12 }}>
      <div
        className="card-flat"
        style={{ padding: '10px 12px', background: 'var(--info-soft)', borderColor: 'transparent' }}
      >
        <div className="hstack" style={{ gap: 8, alignItems: 'flex-start' }}>
          <Icons.IconShield
            size={13}
            style={{ color: 'var(--info)', marginTop: 2, flexShrink: 0 }}
          />
          <div style={{ fontSize: 11.5, color: 'var(--fg)', lineHeight: 1.55 }}>
            设备白名单为<span style={{ fontWeight: 500 }}>设备维度</span> · 当前共{' '}
            <span className="mono" style={{ fontWeight: 600 }}>
              {isRoot ? rows.length : '—'}
            </span>{' '}
            台设备。
            <span style={{ color: 'var(--fg-muted)' }}>
              下表为 <span className="mono">device_whitelist</span>{' '}
              真实明细（契约无设备↔用户关联，不按用户展开）；
              {/* 增删入口 root 专属（后端 requireRole('root')）：非 root 不渲染 ↗ 弹窗入口（F1） */}
              {isRoot ? (
                <>
                  增删见
                  <button
                    className="btn btn-ghost btn-sm"
                    style={{
                      padding: '0 4px',
                      height: 'auto',
                      minHeight: 0,
                      verticalAlign: 'baseline',
                      color: 'var(--info)',
                    }}
                    onClick={onOpenDialog}
                  >
                    设备白名单 ↗
                  </button>
                </>
              ) : (
                <span>增删仅超管可操作</span>
              )}
            </span>
            。
          </div>
        </div>
      </div>

      <div className="card" style={{ overflow: 'hidden' }}>
        <Table className="tbl">
          <thead>
            <tr>
              <th>设备 ID</th>
              <th>归属用户</th>
              <th>备注</th>
              <th>操作员</th>
              <th>添加时间</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((d) => (
              <tr key={d.id}>
                <td className="mono">{d.deviceId}</td>
                {/* 契约无 device↔user 关联 / 操作员字段 → 「—」呈现，不编造 */}
                <td style={{ fontSize: 12 }}>—</td>
                <td style={{ fontSize: 11.5, color: 'var(--fg-muted)' }}>{d.reason || '—'}</td>
                <td className="mono" style={{ fontSize: 11 }}>
                  —
                </td>
                <td className="mono" style={{ fontSize: 11, color: 'var(--fg-muted)' }}>
                  {fmtDate(d.createdAt)}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
        {rows.length === 0 && (
          <div style={{ padding: 28, textAlign: 'center', color: 'var(--fg-faint)', fontSize: 12 }}>
            {isRoot ? '暂无白名单设备' : '设备白名单仅超管可查看（后端 root 专属）'}
          </div>
        )}
      </div>
    </div>
  );
}

// 详情抽屉触达 BONUS / 封禁 / 解禁 / 白名单 / 二次确认时，回调到 UsersPage 顶层受控弹窗与 mutation
interface UserDetailProps {
  user: UserRow;
  initialTab?: string;
  /** 白名单 / 风控策略入口 root 专属（后端 requireRole('root')，前端只做 UX 隐藏） */
  isRoot?: boolean;
  /** 封禁 / 解禁写权限（root/admin）；前端 UX 门控，后端 requireRole 为权威（F4） */
  canModerate?: boolean;
  /** 发放 BONUS 写权限（root）；前端 UX 门控，后端 requireRole('root') 为权威（F4） */
  canGrantBonus?: boolean;
  onClose: () => void;
  onGrantBonus?: (user: UserRow) => void;
  onBan?: (user: UserRow) => void;
  /** 真实解禁（UsersPage 顶层 unbanMutation） */
  onUnban?: (user: UserRow) => void;
  /** 打开设备白名单弹窗（契约无 user↔device 关联 → 手动录入 device_id 的真实 addDevice 路径） */
  onOpenWhitelist?: () => void;
  /** 关抽屉并切到「风控策略」tab（风控详情卡「查看规则配置」入口） */
  onShowPolicy?: () => void;
  onConfirm?: (c: ConfirmState) => void;
}

export function UserDetail({
  user,
  initialTab,
  isRoot = false,
  canModerate = false,
  canGrantBonus = false,
  onClose,
  onGrantBonus,
  onBan,
  onUnban,
  onOpenWhitelist,
  onShowPolicy,
  onConfirm,
}: UserDetailProps) {
  const [tab, setTab] = useState(initialTab || 'profile');
  // 抽屉打开即取用户详情（订阅状态 / 最近登录 IP / 风控 / 两阅读 KPI 富集）；GET /users/:id
  const userDetailQuery = useUserDetail(user.id);
  const userDetail = userDetailQuery.data;
  // 阅读 KPI 展示格式化：typeof number 守卫（apiRequest 不校验响应体，API 版本漂移 / 未加载下字段可能缺）
  // → 缺/非数显示 '—'，防 .toLocaleString 崩详情抽屉（codex adversarial F2）
  const fmtKpiCount = (n: number | undefined | null): string =>
    typeof n === 'number' ? n.toLocaleString() : '—';
  // 充值记录：仅对应 tab 激活时取（GET /api/v2/users/:id/recharge-records）
  const rechargeQuery = useUserRecharges(user.id, tab === 'spend');
  const { data: recharges, total: rechargeTotal } = rechargeQuery;
  const { data: feedbackRecords, total: feedbackTotal } = useUserFeedback(
    user.id,
    tab === 'feedback',
  );
  // 阅读与解锁记录（A3 §7.3）：同样仅对应 tab 激活时取；各 tab 独立页码，切走再回来保留所在页
  const [chapterReadingPage, setChapterReadingPage] = useState(1);
  const [chapterUnlockPage, setChapterUnlockPage] = useState(1);
  const [novelReadingPage, setNovelReadingPage] = useState(1);
  const [consumePage, setConsumePage] = useState(1);
  // 消费记录（A3 §7.2 v2 资产维度）：同为独立页码 + 四态壳
  const consumeQuery = useUserConsumes(user.id, tab === 'consume', consumePage);
  const chapterReadingQuery = useUserChapterReadingRecords(
    user.id,
    tab === 'chapter-reading',
    chapterReadingPage,
  );
  const chapterUnlockQuery = useUserChapterUnlockRecords(
    user.id,
    tab === 'chapter-unlock',
    chapterUnlockPage,
  );
  const novelReadingQuery = useUserNovelReadingRecords(
    user.id,
    tab === 'novel-reading',
    novelReadingPage,
  );

  return (
    <>
      <div
        className="dialog-backdrop"
        onClick={onClose}
        style={{ background: 'oklch(0.1 0.005 250 / 0.3)' }}
      />
      <div className="drawer" role="dialog" aria-modal="true" aria-label="用户详情">
        <div
          style={{
            padding: 18,
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'center',
            gap: 14,
          }}
        >
          <Avatar seed={user.id} size={48} fontSize={18}>
            {user.name[0]}
          </Avatar>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="hstack" style={{ gap: 8 }}>
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>{user.name}</h2>
              <span
                className={`chip ${user.status === '正常' ? 'chip-pos' : user.status === '已封禁' ? 'chip-neg' : 'chip-warn'}`}
              >
                ● {user.status}
              </span>
              <span className="chip">{user.plan}</span>
            </div>
            <div className="mono" style={{ fontSize: 11, color: 'var(--fg-muted)', marginTop: 4 }}>
              {user.id} · {user.email} · {user.appName} ({fmtCountry(user.country)}) · 注册{' '}
              {fmtDateTime(user.registeredAt)}
            </div>
          </div>
          <div className="hstack" style={{ gap: 6 }}>
            {/* 发放 BONUS：root 专属（后端 grant-bonus requireRole('root')）→ 非 root 禁用（F4） */}
            {onGrantBonus && (
              <button
                className="btn btn-sm"
                disabled={!canGrantBonus}
                title={canGrantBonus ? undefined : FORBIDDEN_ROLE_TITLE}
                style={canGrantBonus ? undefined : { opacity: 0.55, cursor: 'not-allowed' }}
                onClick={() => onGrantBonus?.(user)}
              >
                <Icons.IconCoin size={11} />
                发放 BONUS
              </button>
            )}
            {/* 封禁 / 解禁：root/admin（后端 ban/unban requireRole）→ 其它角色禁用（F4） */}
            {(onBan || onUnban) &&
              (user.status === '已封禁' ? (
                <button
                  className="btn btn-sm"
                  disabled={!canModerate || !onUnban || !onConfirm}
                  title={canModerate ? undefined : FORBIDDEN_ROLE_TITLE}
                  style={canModerate ? undefined : { opacity: 0.55, cursor: 'not-allowed' }}
                  onClick={() =>
                    onConfirm?.({
                      title: `解禁 ${user.name}？`,
                      message: '解禁后用户可立即恢复登录使用，本次解禁将记录到操作日志。',
                      tone: 'default',
                      confirmLabel: '确认解禁',
                      // 真实接线：POST /users/:id/unban（UsersPage 顶层 mutation）
                      onConfirm: () => onUnban?.(user),
                    })
                  }
                >
                  <Icons.IconCheck size={11} />
                  解禁
                </button>
              ) : (
                <button
                  className="btn btn-sm btn-danger"
                  disabled={!canModerate || !onBan}
                  title={canModerate ? undefined : FORBIDDEN_ROLE_TITLE}
                  style={canModerate ? undefined : { opacity: 0.55, cursor: 'not-allowed' }}
                  onClick={() => onBan?.(user)}
                >
                  <Icons.IconBan size={11} />
                  封禁
                </button>
              ))}
            <button aria-label="关闭用户详情" className="btn btn-ghost btn-icon" onClick={onClose}>
              <Icons.IconX size={14} />
            </button>
          </div>
        </div>

        {/* Risk banner */}
        {user.risk && (
          <div
            style={{
              padding: '10px 18px',
              background: 'var(--neg-soft)',
              borderBottom: '1px solid var(--border)',
            }}
          >
            <div className="hstack" style={{ gap: 10 }}>
              <Icons.IconShield size={14} style={{ color: 'var(--neg)' }} />
              <div style={{ flex: 1, fontSize: 12 }}>
                <span style={{ fontWeight: 500, color: 'var(--neg)' }}>触发风控</span>
                <span style={{ color: 'var(--fg-muted)', marginLeft: 6 }}>
                  {user.risk} · 已自动加入观察列表，进一步触发将自动封禁
                </span>
              </div>
              {/* 契约无 user↔device 关联（拿不到主设备 device_id）→ 打开白名单弹窗手动录入；
                  端点 root 专属，非 root 不展示入口 */}
              {isRoot && (
                <button className="btn btn-sm" onClick={onOpenWhitelist}>
                  加入白名单
                </button>
              )}
            </div>
          </div>
        )}

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(5, 1fr)',
            padding: '12px 18px',
            gap: 12,
            borderBottom: '1px solid var(--border)',
          }}
        >
          <Kpi label="COIN 余额" v={user.coin.toLocaleString()} />
          <Kpi label="BONUS 余额" v={user.bonus.toLocaleString()} />
          {/* 累计充值 per-currency（catch-up #130）：adapter 已含币种符号 / '—'，直接渲染 */}
          <Kpi label="累计充值" v={user.totalSpend} />
          {/* 两阅读 KPI（ADM-USR-S002，A3 §4，#320）：详情端点 read_chapter_count / paid_unlocked_chapter_count；
              口径独立（实际阅读量 vs 付费深度）；未加载/缺字段 → '—'（fmtKpiCount typeof 守卫） */}
          <Kpi label="阅读章节" v={fmtKpiCount(userDetail?.read_chapter_count)} />
          <Kpi label="付费解锁" v={fmtKpiCount(userDetail?.paid_unlocked_chapter_count)} />
        </div>

        <div
          style={{
            padding: '0 18px',
            // 底线用内阴影而非 border：overflowX:auto 会把 overflow-y 也算成 auto，按钮若再用
            // marginBottom:-1 压底线就会多出 1px 纵向溢出（出滚动条、裁掉激活下划线）
            boxShadow: 'inset 0 -1px 0 var(--border)',
            display: 'flex',
            // 8 个 tab 在窄屏（drawer 92vw）放不下 → 横向滚动而非换行挤压
            overflowX: 'auto',
          }}
        >
          {[
            { id: 'profile', label: '资料' },
            { id: 'spend', label: '充值记录' },
            { id: 'consume', label: '消费记录' },
            { id: 'chapter-reading', label: '章节阅读记录' },
            { id: 'chapter-unlock', label: '章节解锁记录' },
            { id: 'novel-reading', label: '小说阅读记录' },
            { id: 'sub', label: '订阅' },
            { id: 'feedback', label: '反馈' },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              style={{
                flexShrink: 0,
                whiteSpace: 'nowrap',
                border: 0,
                background: 'transparent',
                padding: '10px 14px',
                font: 'inherit',
                fontSize: 12.5,
                cursor: 'pointer',
                color: tab === t.id ? 'var(--fg-strong)' : 'var(--fg-muted)',
                fontWeight: tab === t.id ? 500 : 400,
                borderBottom: tab === t.id ? '2px solid var(--accent)' : '2px solid transparent',
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div style={{ flex: 1, overflow: 'auto', padding: 18 }}>
          {tab === 'profile' && (
            <div className="vstack" style={{ gap: 12 }}>
              <Row
                k="展示用户 ID"
                v={formatDisplayUserIdForPresentation(
                  userDetail?.display_user_id ?? user.displayUserId,
                )}
                mono
              />
              <Row k="内部用户 ID" v={user.id} mono />
              <UserHeldTags
                userId={user.id}
                tags={userDetail?.tags}
                canManage={canModerate}
                readState={userDetailQuery}
              />
              <Row k="邮箱" v={user.email} mono />
              {/* 邮箱验证态（dev-docs #450 A3 §5.2）：绑定态仅由 email_verified_at 判定，
                  email 非空不代表已绑定；有值=已验证时间，null=未验证/未绑定。 */}
              <Row
                k="邮箱验证"
                v={
                  // 加载中（userDetail 未到）显 '—'，与同组 device_id/last_login_ip 一致——
                  // 避免对已验证用户在 fetch 窗口内假显「未验证」；加载完成且 null 才是真未验证。
                  !userDetail
                    ? '—'
                    : userDetail.email_verified_at
                      ? fmtDateTime(userDetail.email_verified_at)
                      : '未验证'
                }
              />
              <Row k="所属 App" v={user.appName} />
              <Row k="地区" v={fmtCountry(user.country)} />
              {/* 当前内容语言（A3 §7.1）= users.last_seen_language。只有显式 null 才是从未上报；
                  详情未到或响应缺该键（web 先于 server 部署的版本漂移窗口，apiRequest 不校验响应体）一律 '—'，
                  不对已上报用户误断言「未上报」。 */}
              <Row
                k="当前内容语言"
                v={
                  typeof userDetail?.language === 'string' && userDetail.language
                    ? fmtLang(userDetail.language)
                    : userDetail?.language === null
                      ? '未上报'
                      : '—'
                }
              />
              {/* 注册语言（A3 §7.1）= users.registration_language，建号时写入、此后不变。显式 null =
                  本列上线前的存量用户未记录；详情未到 / 响应缺键同上一律 '—'。 */}
              <Row
                k="注册语言"
                v={
                  typeof userDetail?.registration_language === 'string' &&
                  userDetail.registration_language
                    ? fmtLang(userDetail.registration_language)
                    : userDetail?.registration_language === null
                      ? '未记录'
                      : '—'
                }
              />
              <Row k="注册时间" v={fmtDateTime(user.registeredAt)} mono />
              <Row k="最近活跃" v={fmtDateTime(user.lastSeen)} />
              <Row k="订阅等级" v={user.plan} />
              {/* 设备 ID / 最近 IP：均接真详情 DTO。device_id = 用户最近 session 的 user_sessions.device_id
                  （已镜像列只读聚合），无 session → "—"。注：此为「最近设备」展示，与下方 root 专属「设备白名单」
                  （手动录入 device_id 做封禁豁免）是两件事。 */}
              <Row k="设备 ID" v={userDetail?.device_id ?? '—'} mono />
              <Row k="最近 IP" v={userDetail?.last_login_ip ?? '—'} mono />

              {/* ADM-USR-S002 · 触发风控原因详情（design users.jsx 459-487）。
                  原型用 user.risk 字符串反推命中规则（P-44 反模式）；真 DTO（risk_status/risk_reason）
                  无命中规则关联数据源 → 触发原因显真值，规则关联诚实空态，不编造 join。
                  仅在**真实风控信号**（user.risk）时展示——手动封禁（status≠正常但无风控命中）不显
                  「命中规则」卡，避免对非风控封禁误导（codex #54 CLI）。 */}
              {user.risk && (
                <div className="card-flat" style={{ padding: 0, overflow: 'hidden', marginTop: 4 }}>
                  <div
                    className="hstack"
                    style={{ padding: '10px 12px', background: 'var(--neg-soft)', gap: 8 }}
                  >
                    <Icons.IconShield size={13} style={{ color: 'var(--neg)' }} />
                    <span style={{ fontWeight: 500, fontSize: 12.5 }}>风控详情 · 命中规则</span>
                    <div style={{ flex: 1 }} />
                    <span
                      className={`chip ${user.status === '已封禁' ? 'chip-neg' : 'chip-warn'}`}
                      style={{ fontSize: 10 }}
                    >
                      {user.status === '已封禁' ? '自动封禁' : '风控观察'}
                    </span>
                  </div>
                  <div style={{ padding: '6px 12px 10px' }}>
                    <Row k="触发原因" v={user.risk || '—'} mono />
                    <Row k="命中规则" v="— · 契约暂无命中规则关联数据源" mono />
                    <Row k="执行动作" v={user.status === '已封禁' ? '自动封禁' : '风控观察'} />
                    <Row
                      k="建议动作"
                      v={
                        user.status === '已封禁'
                          ? '核实后可解禁 / 设备加入白名单'
                          : '持续观察 / 设备加入白名单'
                      }
                    />
                    {/* 规则配置 / 白名单均 root 专属（后端 requireRole('root')，前端只做 UX 隐藏） */}
                    {isRoot && (
                      <div className="hstack" style={{ gap: 6, paddingTop: 8 }}>
                        <button className="btn btn-sm" onClick={onShowPolicy}>
                          <Icons.IconShield size={11} />
                          查看规则配置
                        </button>
                        <button className="btn btn-sm" onClick={onOpenWhitelist}>
                          加入白名单
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
          {tab === 'spend' &&
            (rechargeQuery.isLoading ? (
              <div className="card" style={{ padding: 28 }}>
                加载中…
              </div>
            ) : rechargeQuery.isError ? (
              <div className="card" style={{ padding: 28 }}>
                <p>
                  {rechargeQuery.serviceUpdating
                    ? '服务更新中'
                    : rechargeQuery.error instanceof Error
                      ? rechargeQuery.error.message
                      : '充值记录加载失败'}
                </p>
                <button className="btn btn-sm" onClick={() => void rechargeQuery.reload()}>
                  重新加载
                </button>
              </div>
            ) : recharges.length === 0 ? (
              <div
                className="card"
                style={{
                  padding: 28,
                  textAlign: 'center',
                  color: 'var(--fg-muted)',
                  fontSize: 12.5,
                }}
              >
                无充值记录
              </div>
            ) : (
              <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                <Table className="tbl">
                  <thead>
                    <tr>
                      <th>时间</th>
                      <th>类型 / 商品</th>
                      <th className="num">原币金额</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recharges.map((r) => (
                      <tr key={r.id}>
                        <td className="mono">{fmtDateTime(r.occurred_at)}</td>
                        <td className="mono">
                          {r.kind === 'REFUND' ? '退款' : '购买'} · {r.description ?? '—'}
                        </td>
                        <td className="num mono">
                          {fmtSpendByCurrency([
                            { currency: r.currency, amount_minor_units: r.amount_minor_units },
                          ])}{' '}
                          {r.currency}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
                {rechargeTotal > recharges.length && (
                  <div style={{ padding: 8, fontSize: 11, color: 'var(--fg-faint)' }}>
                    显示最近 {recharges.length} / 共 {rechargeTotal} 条
                  </div>
                )}
              </div>
            ))}
          {tab === 'consume' && (
            <UserRecordTabBody
              query={consumeQuery}
              page={consumePage}
              onPageChange={setConsumePage}
              emptyText="无消费记录"
              errorText="消费记录加载失败"
            >
              <thead>
                <tr>
                  <th>类型</th>
                  <th>对象</th>
                  <th className="num">消费</th>
                  <th>时间</th>
                </tr>
              </thead>
              <tbody>
                {consumeQuery.data.map((r) => (
                  // Coin / Bonus 两表主键各自唯一，合并列表以资产 + id 作 key
                  <tr key={`${r.asset_type}-${r.id}`}>
                    <td>
                      <span className="chip" style={{ fontSize: 10 }}>
                        {consumeTypeLabel(r)}
                      </span>
                    </td>
                    <td>{r.description ?? '—'}</td>
                    {/* 同原型：Coin 为默认资产只写数量，Bonus 写作「-数量 BONUS」 */}
                    <td className="num">
                      {r.asset_type === 'BONUS' ? `-${r.amount} BONUS` : r.amount}
                    </td>
                    <td className="mono" style={{ fontSize: 11, color: 'var(--fg-muted)' }}>
                      {fmtDateTime(r.occurred_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </UserRecordTabBody>
          )}
          {tab === 'chapter-reading' && (
            <UserRecordTabBody
              query={chapterReadingQuery}
              page={chapterReadingPage}
              onPageChange={setChapterReadingPage}
              emptyText="无章节阅读记录"
              errorText="章节阅读记录加载失败"
            >
              <thead>
                <tr>
                  {/* 阅读日按用户日切时区切日，首次计入按本机时区显示 —— 两者可能落在不同日历日 */}
                  <th>阅读日（用户时区）</th>
                  <th>书 · 章节</th>
                  <th>当日首次计入（本机时间）</th>
                </tr>
              </thead>
              <tbody>
                {chapterReadingQuery.data.map((r) => (
                  <tr key={r.id}>
                    <td className="mono">{r.date_key}</td>
                    <td>
                      {r.novel_title ?? '—'} ·{' '}
                      {r.chapter_index === null ? '—' : `第${r.chapter_index}章`}{' '}
                      {r.chapter_title ?? ''}
                    </td>
                    <td className="mono">{fmtDateTime(r.read_at)}</td>
                  </tr>
                ))}
              </tbody>
            </UserRecordTabBody>
          )}
          {tab === 'chapter-unlock' && (
            <UserRecordTabBody
              query={chapterUnlockQuery}
              page={chapterUnlockPage}
              onPageChange={setChapterUnlockPage}
              emptyText="无章节解锁记录"
              errorText="章节解锁记录加载失败"
            >
              <thead>
                <tr>
                  <th>时间</th>
                  <th>书 · 章节</th>
                  <th className="num">Coin</th>
                  <th className="num">Bonus</th>
                  <th>方式</th>
                </tr>
              </thead>
              <tbody>
                {chapterUnlockQuery.data.map((r) => (
                  <tr key={r.id}>
                    <td className="mono">{fmtDateTime(r.unlocked_at)}</td>
                    <td>
                      {r.novel_title} · 第{r.chapter_index}章 {r.chapter_title}
                    </td>
                    <td className="num mono">{r.coin_used}</td>
                    <td className="num mono">{r.bonus_used}</td>
                    <td>{r.auto_unlocked ? '自动解锁' : '手动解锁'}</td>
                  </tr>
                ))}
              </tbody>
            </UserRecordTabBody>
          )}
          {tab === 'novel-reading' && (
            <UserRecordTabBody
              query={novelReadingQuery}
              page={novelReadingPage}
              onPageChange={setNovelReadingPage}
              emptyText="无小说阅读记录"
              errorText="小说阅读记录加载失败"
            >
              <thead>
                <tr>
                  <th>书</th>
                  <th>读到</th>
                  <th>最近阅读</th>
                  <th>首次阅读</th>
                </tr>
              </thead>
              <tbody>
                {novelReadingQuery.data.map((r) => (
                  <tr key={r.id}>
                    <td>
                      {r.novel_title ?? '—'}
                      {r.novel_locale && (
                        <span className="mono" style={{ marginLeft: 6, color: 'var(--fg-muted)' }}>
                          {fmtLang(r.novel_locale)}
                        </span>
                      )}
                      {/* 已发布章数是计数、续读位置是 chapter_index 序号，断号时不可比 → 分开展示，不拼成「第X / 共Y」 */}
                      {r.novel_total_chapters !== null && (
                        <div style={{ fontSize: 11.5, color: 'var(--fg-muted)' }}>
                          已发布 {r.novel_total_chapters} 章
                        </div>
                      )}
                    </td>
                    <td>
                      第{r.last_chapter_index}章
                      {r.last_chapter_title && (
                        <div style={{ fontSize: 11.5, color: 'var(--fg-muted)' }}>
                          {r.last_chapter_title}
                        </div>
                      )}
                    </td>
                    <td className="mono">{fmtDateTime(r.last_read_at)}</td>
                    <td className="mono">{fmtDateTime(r.first_read_at)}</td>
                  </tr>
                ))}
              </tbody>
            </UserRecordTabBody>
          )}
          {tab === 'sub' &&
            (userDetail?.subscription_status ? (
              <div className="card" style={{ padding: 16 }}>
                <Row k="订阅状态" v={userDetail.subscription_status.status} />
                <Row k="产品" v={userDetail.subscription_status.product_id} mono />
                <Row k="商店" v={userDetail.subscription_status.store} />
                <Row k="自动续订" v={userDetail.subscription_status.auto_renew ? '是' : '否'} />
                <Row k="试用中" v={userDetail.subscription_status.is_in_trial ? '是' : '否'} />
                <Row k="开始" v={fmtDateTime(userDetail.subscription_status.starts_at)} mono />
                <Row k="到期" v={fmtDateTime(userDetail.subscription_status.expires_at)} mono />
                <Row k="续订次数" v={String(userDetail.subscription_status.renewal_count)} />
                <Row
                  k="金额"
                  v={`${userDetail.subscription_status.currency} ${(
                    Number(userDetail.subscription_status.amount_minor_units) / 100
                  ).toFixed(2)}`}
                  mono
                />
              </div>
            ) : (
              <div
                className="card"
                style={{
                  padding: 28,
                  textAlign: 'center',
                  color: 'var(--fg-muted)',
                  fontSize: 12.5,
                }}
              >
                无活跃订阅
              </div>
            ))}
          {tab === 'feedback' &&
            (feedbackRecords.length === 0 ? (
              <div
                className="card"
                style={{
                  padding: 28,
                  textAlign: 'center',
                  color: 'var(--fg-muted)',
                  fontSize: 12.5,
                }}
              >
                无反馈记录
              </div>
            ) : (
              <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                <Table className="tbl">
                  <thead>
                    <tr>
                      <th>时间</th>
                      <th>主题</th>
                      <th>分类</th>
                      <th>状态</th>
                    </tr>
                  </thead>
                  <tbody>
                    {feedbackRecords.map((r) => (
                      <tr key={r.id}>
                        <td className="mono">{fmtDateTime(r.created_at)}</td>
                        <td>{r.subject}</td>
                        <td>{r.category}</td>
                        <td>{r.status}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
                {feedbackTotal > feedbackRecords.length && (
                  <div style={{ padding: 8, fontSize: 11, color: 'var(--fg-faint)' }}>
                    显示最近 {feedbackRecords.length} / 共 {feedbackTotal} 条
                  </div>
                )}
              </div>
            ))}
        </div>
      </div>
    </>
  );
}

function Kpi({ label, v }: { label: string; v: ReactNode }) {
  return (
    <div>
      <div className="stat-label">{label}</div>
      <div className="stat-value" style={{ fontSize: 19, marginTop: 4 }}>
        {v}
      </div>
    </div>
  );
}

/**
 * 消费记录「类型」标签：来源码（Coin 行 coin_transactions.source / Bonus 行 bonus_consumptions.reason，
 * 钱包字典 50-钱包 §3 / §5）→ 中文；Bonus 章节解锁按原型称「BONUS 抵扣」。字典新增的未知码原样展示。
 */
const CONSUME_SOURCE_LABEL: Record<string, string> = {
  CHAPTER_UNLOCK: '章节解锁',
  REFUND: '退款回滚',
  ADMIN_ADJUST: '运营调整',
  ADMIN_TRANSFER: '跨账号转移',
  ADMIN_REVOKE: '运营扣回',
  EXPIRED: '过期清算',
};

function consumeTypeLabel(r: { asset_type: string; source: string }): string {
  if (r.asset_type === 'BONUS' && r.source === 'CHAPTER_UNLOCK') return 'BONUS 抵扣';
  return (
    (Object.hasOwn(CONSUME_SOURCE_LABEL, r.source) && CONSUME_SOURCE_LABEL[r.source]) || r.source
  );
}

/**
 * 详情抽屉记录 tab 的加载 / 失败 / 空 / 表格四态壳（与「充值记录」tab 同款 card + tbl，不引新视觉）。
 * 总数超过一页时表格下方接 ~/ui/Pagination 翻页。
 */
function UserRecordTabBody<T>({
  query,
  page,
  onPageChange,
  emptyText,
  errorText,
  children,
}: {
  query: UserRecordTabQuery<T>;
  page: number;
  onPageChange: (page: number) => void;
  emptyText: string;
  errorText: string;
  children: ReactNode;
}) {
  const lastPage = Math.max(1, Math.ceil(query.total / USER_RECORD_TAB_PAGE_SIZE));
  // 页码越界（停在后面的页期间总数变少，例如用户在 C 端清除了阅读记录）：只在本页结果落定后
  // 才收回到最后一页 —— 在途 / 暂停中的重取、占位数据里的旧 total 都不能作数
  const pageOutOfRange = query.isResultSettled && page > lastPage;
  useEffect(() => {
    if (pageOutOfRange) onPageChange(lastPage);
  }, [pageOutOfRange, lastPage, onPageChange]);

  // 以下情形都还没有可信的本页结果，按加载中处理，不能落进空态说「无记录」：
  // 首次加载或离线暂停（isPending）；翻页占位而占位本身为空（如收回时拿越界页的空结果占位）；
  // 当前页按已知 total 越界、正等重取或收回
  const awaitingResult =
    query.isPending ||
    (query.isPlaceholderData && query.data.length === 0) ||
    (!query.isError && page > lastPage);
  const showingPlaceholderRows =
    !awaitingResult && !query.isError && query.isPlaceholderData && query.data.length > 0;

  let body: ReactNode;
  if (awaitingResult) {
    body = (
      <div className="card" style={{ padding: 28 }}>
        加载中…
      </div>
    );
  } else if (query.isError) {
    body = (
      <div className="card" style={{ padding: 28 }}>
        <p>
          {query.serviceUpdating
            ? '服务更新中'
            : `${errorText}${query.error instanceof Error && query.error.message ? `：${query.error.message}` : ''}`}
        </p>
        <div className="hstack" style={{ gap: 8 }}>
          <button className="btn btn-sm" onClick={query.reload}>
            重新加载
          </button>
          {/* 后面的页失败时错误卡片取代了翻页器，给出回到第 1 页的出口 */}
          {page > 1 && (
            <button className="btn btn-sm btn-ghost" onClick={() => onPageChange(1)}>
              回到第 1 页
            </button>
          )}
        </div>
      </div>
    );
  } else if (query.data.length === 0) {
    body = (
      <div
        className="card"
        style={{ padding: 28, textAlign: 'center', color: 'var(--fg-muted)', fontSize: 12.5 }}
      >
        {emptyText}
      </div>
    );
  } else {
    body = (
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {/* 翻页占位期：表格仍是上一页的行，弱化显示避免当成新页阅读 */}
        <div
          aria-busy={query.isPlaceholderData}
          style={query.isPlaceholderData ? { opacity: 0.5 } : undefined}
        >
          <Table className="tbl">{children}</Table>
        </div>
        {query.total > USER_RECORD_TAB_PAGE_SIZE && (
          <Pagination
            total={query.total}
            page={page}
            pageSize={USER_RECORD_TAB_PAGE_SIZE}
            onChange={onPageChange}
          />
        )}
      </div>
    );
  }

  return (
    <>
      {/* 占位期的文字提示只给读屏（视觉上已有表格弱化），不占位也就不会压到翻页器或 sticky 表头。
          status 节点放在四态共用的外层常驻、只切文字：从加载 / 失败卡切到占位表格时才会被播报 */}
      <span role="status" style={VISUALLY_HIDDEN_STYLE}>
        {showingPlaceholderRows ? '更新中…' : ''}
      </span>
      {body}
    </>
  );
}

function Row({ k, v, mono }: { k: string; v: ReactNode; mono?: boolean }) {
  return (
    <div
      className="hstack"
      style={{ gap: 12, padding: '8px 0', borderBottom: '1px dashed var(--border)' }}
    >
      <div style={{ width: 110, fontSize: 12, color: 'var(--fg-muted)' }}>{k}</div>
      <div style={{ flex: 1, fontSize: 12.5 }} className={mono ? 'mono' : ''}>
        {v}
      </div>
    </div>
  );
}

export { UsersPage };
